"use client";

// 2026-09-16 redesign premium (websearch 2026 "best 404/error page UX Linear Vercel Stripe"):
// composición centrada respirable — pill de estado + título expresivo + 1 CTA
// primario (reintentar) + enlaces quiet (inicio/contacto) + ref copiable para
// soporte. Sin animación JS (solo CSS page-enter, respeta reduced-motion).
// Copy 100% i18n existente (error*/docsHelpContact/chatChatCopy — 14 locales,
// sin keys nuevas). MINIMALISTA PRO: 2-3 controles visibles.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Copy,
  MessageCircle,
  RotateCcw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
// 2026-10-09 bundle-budget: `localePath` vive en el módulo SSOT
// `@/lib/i18n/locales` (sin diccionarios). Importarlo desde `@/lib/i18n`
// (index) evaluaba el módulo completo —que construye `dictionaries` con las
// 14 locales fusionadas (~2.7 MB de fuente)— y lo metía en el chunk COMPARTIDO
// de TODAS las rutas vía `error.tsx` raíz: medido 2026-10-09, ese chunk eran
// 3109 KB del first-load de `/` (4937 KB) y de `/chat` (4658 KB). Mismo
// patrón que NotFoundClient.tsx:8 y el resto de islas cliente.
import { localePath } from "@/lib/i18n/locales";
import { useLocale, useT } from "@/lib/use-i18n";
import {
  isChunkLoadFailure,
  shouldSuppressClientErrorReport,
} from "@/lib/browser-console-noise";
import { reportClientError } from "@/lib/client-error-reporting";
import { PageAmbient } from "@/components/layout/PageAmbient";

interface RouteErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
  showHomeLink?: boolean;
  showDigest?: boolean;
}

export function RouteErrorPage({
  error,
  reset,
  showHomeLink = false,
  showDigest = false,
}: RouteErrorPageProps) {
  const t = useT();
  const locale = useLocale();
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (process.env.NODE_ENV === "development") console.error(error);
    const ua = navigator.userAgent;
    const msg = error.message || String(error);
    // Bots / usage-poll noise / CSP eval — never page Discord.
    if (shouldSuppressClientErrorReport(msg, ua)) return;
    // Chunk miss: ErrorBoundary already auto-reloads; route error is residual noise.
    if (isChunkLoadFailure(msg) || isChunkLoadFailure(error.name)) return;
    void reportClientError({
      message: error,
      stack: error.stack,
      digest: error.digest,
      locationHref: window.location.href,
      pathname: window.location.pathname,
      userAgent: ua,
    });
  }, [error]);

  // 2026-09-22 FASE 3 FIX P2 a11y (research 2026 WCAG 2.2 2.4.3 foco inaugural
  // + 3.3.1 errores: role="alert" sin foco no mueve el cursor AT — el lector
  // no anuncia el error hasta que el usuario navega. El contenedor con
  // tabIndex -1 recibe foco en mount; el alert lo anuncia).
  // 2026-10-02 fix dev · el anillo de foco se pintaba alrededor del bloque
  // entero (~576px) de la página de error: foco inaugural por navegación
  // (WCAG 2.2 2.4.3), no por una acción del usuario sobre esta superficie,
  // y aquí la superficie ES la página completa, así que no hay contexto que
  // localizar. Distinto del error en línea de un formulario (AuthForm
  // role="alert"), donde el surroundings no cambia y el anillo sí Locate:
  // ese conserva el suyo a propósito. `outline-none` a solas era inerte — la
  // utilidad vive en `@layer utilities` (Tailwind v4) y este `:focus-visible`
  // global de src/app/globals.css está SIN capa, así que ganaba la
  // declaración unlayered por CSS Cascade 5 §6.4; con `!important` el orden
  // de capas se invierte. `shadow-none!` cierra además el halo de 4px.
  useEffect(() => {
    containerRef.current?.focus({ preventScroll: true });
  }, []);

  const onCopyDigest = async () => {
    if (!error.digest) return;
    try {
      await navigator.clipboard.writeText(error.digest);
    } catch {
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <main
      id="main"
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-4 py-16"
    >
      <PageAmbient intensity="soft" />
      {/* 2026-09-22 FASE 3 FIX P2 tokens/perf (research MDN 2026-04-20:
          blur 40-120px sobre fondo opaco = costo GPU muerto sin ganancia
          visual): el glow danger blur-[120px] se elimina — PageAmbient soft
          basta como única capa de ambiente (1 capa CSS sin JS). */}
      <div
        className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] opacity-[0.16] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,black_20%,transparent_75%)]" />
      </div>

      <div
        ref={containerRef}
        tabIndex={-1}
        className="page-enter relative z-[1] w-full max-w-xl text-center outline-none! shadow-none!"
        role="alert"
        aria-labelledby="route-error-title"
      >
        {/* Pill de estado — 2026-09-24 FASE 7 tipografía (research 2026 UI type
            scale: secundario 11px fijo + tracking cap 0.08em SSOT, sin competir
            con h1 display semibold foreground). */}
        <p className="inline-flex items-center gap-2 rounded-full border border-[var(--danger)]/25 bg-[var(--danger)]/[0.08] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--danger)]">
          <span className="relative flex size-1.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--danger)] opacity-60 motion-reduce:animate-none" />
            <span className="relative inline-flex size-1.5 rounded-full bg-[var(--danger)]" />
          </span>
          {t("errorKicker")}
        </p>

        {/* Icono contenido */}
        {/* 2026-09-24 FASE 3 FIX design-auth-tokens (research 2026
            single-source tokens: border-white/10 fijo → var(--border),
            visible en light/dark; paridad BannedPageClient:105). */}
        <div className="mx-auto mt-7 flex size-16 items-center justify-center rounded-2xl border border-[var(--border)] bg-[var(--danger)]/10 shadow-[0_0_50px_color-mix(in_srgb,var(--danger)_12%,transparent),inset_0_1px_0_rgba(255,255,255,0.12)]">
          <AlertTriangle
            className="size-7 text-[var(--danger)]"
            aria-hidden="true"
          />
        </div>

        {/* 2026-10-03 RUN12 FASE 8 N6 [route-error-h1-step-4] (research 2026
            Utopia, reutiliza auditoría F8: text-4xl/sm:text-5xl 36/48px →
            step-4 único 33.63/39.83/50.05px; tinta foreground + max-w-20ch +
            balance intactos ⇒ sin overflow; flujo centrado sin alturas fijas;
            large-text ≥24px en los 3 viewports ⇒ AA intacto). */}
        <h1
          id="route-error-title"
          className="mx-auto mt-6 max-w-[20ch] text-balance text-[var(--step-4)] font-bold tracking-tight text-foreground"
        >
          {t("errorTitle")}
        </h1>
        <p className="mx-auto mt-4 max-w-[42ch] text-pretty text-[var(--step--1)] leading-7 text-muted-foreground">
          {/* 2026-10-03 RUN12 FASE 8 N6 (cuerpo step--1 aplicado; h1 migrado a
              step-4 en el nodo N6 del mismo run). */}
          {t("errorBody")}
        </p>

        {/* Acciones: 1 primaria + quiet links */}
        <div className="mt-8 flex flex-col items-center gap-4">
          <Button
            variant="accent"
            onClick={reset}
            className="btn-shimmer spring-press h-12 rounded-full px-7 text-[15px] font-medium"
          >
            <RotateCcw className="size-4" aria-hidden="true" />
            {t("errorRetry")}
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm">
            {showHomeLink ? (
              <Link
                href="/"
                className="inline-flex items-center gap-1.5 font-medium text-muted-foreground underline decoration-[var(--border-strong)] underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
              >
                <ArrowLeft className="size-3.5" aria-hidden="true" />
                {t("errorHome")}
              </Link>
            ) : null}
            <Link
              href={localePath(locale, "/contact")}
              className="inline-flex items-center gap-1.5 font-medium text-muted-foreground underline decoration-[var(--border-strong)] underline-offset-4 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              <MessageCircle className="size-3.5" aria-hidden="true" />
              {t("docsHelpContact")}
            </Link>
          </div>
        </div>

        {showDigest && error.digest ? (
          <div className="mx-auto mt-8 inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card py-1.5 pl-4 pr-1.5">
            <span className="truncate font-mono text-[11px] text-muted-foreground">
              {t("errorRef")}: {error.digest}
            </span>
            <button
              type="button"
              onClick={onCopyDigest}
              aria-label={t("chatChatCopy")}
              title={t("chatChatCopy")}
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
            >
              {copied ? (
                <Check className="size-3.5" aria-hidden="true" />
              ) : (
                <Copy className="size-3.5" aria-hidden="true" />
              )}
            </button>
            <span aria-live="polite" className="sr-only">
              {copied ? t("chatChatCopied") : ""}
            </span>
          </div>
        ) : null}
      </div>
    </main>
  );
}
